import React from 'react';
import {
  Star,
  Clock,
  Home,
  Building,
  TrendingUp,
  Heart,
  Key,
  Shield,
  GraduationCap,
  Laptop,
  Stethoscope,
  Briefcase,
  Building2,
  BarChart,
  Home as HomeIcon,
  LineChart,
  Wallet
} from 'lucide-react';

interface SocialProofProps {
  userType: 'renter' | 'agent' | 'landlord';
}

const socialProofData = {
  renter: {
    stats: [
      { value: '15,000+', label: 'Active Listings', icon: Home },
      { value: '48 Hours', label: 'Average Search Time', icon: Clock },
      { value: '98%', label: 'Search Success Rate', icon: TrendingUp }
    ],
    testimonials: [
      {
        name: 'Sarah',
        role: 'Student at UCD',
        text: 'Found my dream apartment in Dublin within a week! The filtering options made it so easy to find exactly what I was looking for.',
        rating: 5,
        icon: GraduationCap
      },
      {
        name: "James",
        role: 'Software Engineer',
        text: 'The instant alerts helped me be first to view a property that perfectly matched my criteria. Signed the lease the same day!',
        rating: 5,
        icon: Laptop
      },
      {
        name: 'Maria',
        role: 'Healthcare Professional',
        text: 'After searching for months on other platforms, I found my perfect home through RentSmart in just two weeks.',
        rating: 5,
        icon: Stethoscope
      }
    ]
  },
  agent: {
    stats: [
      { value: '€2.5M+', label: 'Monthly Transactions', icon: TrendingUp },
      { value: '2,500+', label: 'Properties Managed', icon: Building },
      { value: '99%', label: 'Client Satisfaction', icon: Heart }
    ],
    testimonials: [
      {
        name: 'Michael',
        role: 'Senior Property Agent',
        text: 'RentSmart has revolutionized how we manage listings. The automated showing scheduler saves us countless hours every week.',
        rating: 5,
        icon: Briefcase
      },
      {
        name: 'Emma',
        role: 'Real Estate Agent',
        text: "The quality of leads we get through RentSmart is exceptional. The platform's verification process ensures we only deal with serious inquiries.",
        rating: 5,
        icon: Building2
      },
      {
        name: 'David',
        role: 'Property Manager',
        text: "The analytics dashboard helps us make data-driven decisions. We've increased our occupancy rates by 25% since using RentSmart.",
        rating: 5,
        icon: BarChart
      }
    ]
  },
  landlord: {
    stats: [
      { value: '72 Hours', label: 'Average Listing Time', icon: Clock },
      { value: '95%', label: 'Occupancy Rate', icon: Key },
      { value: '100%', label: 'Verified Tenants', icon: Shield }
    ],
    testimonials: [
      {
        name: 'Paul',
        role: 'Property Owner',
        text: 'Listed my property and had quality tenants within 48 hours. The tenant verification process gives me peace of mind.',
        rating: 5,
        icon: HomeIcon
      },
      {
        name: 'Lisa',
        role: 'Investment Property Owner',
        text: "Managing multiple properties used to be a headache. RentSmart's landlord dashboard makes it effortless.",
        rating: 5,
        icon: LineChart
      },
      {
        name: 'Thomas',
        role: 'Property Investor',
        text: 'The messaging system streamlines communication with potential tenants. I love how organized and efficient the whole process is.',
        rating: 5,
        icon: Wallet
      }
    ]
  }
};

const SocialProof: React.FC<SocialProofProps> = ({ userType }) => {
  const data = socialProofData[userType];

  return (
    <div className="mt-3 sm:mt-4 w-full bg-gradient-to-b from-indigo-50 to-white py-10 sm:py-12 md:py-16 rounded-2xl sm:rounded-3xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Testimonials Section */}
        <div>
          <div className="mb-3 sm:mb-4 text-center">
            <span className="inline-block bg-indigo-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-wide mb-3 sm:mb-4">
              Success Stories
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-center text-indigo-900 mb-3 sm:mb-4">
            What Our {userType.charAt(0).toUpperCase() + userType.slice(1)}s Say
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 text-center max-w-2xl mx-auto mb-8 sm:mb-10 md:mb-12 px-4">
            Real experiences from our valued community members
          </p>
          <div className="grid grid-cols-1 gap-5 sm:gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {data.testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-white to-indigo-50 rounded-lg sm:rounded-xl shadow-xl hover:shadow-2xl p-5 sm:p-6 md:p-8 transition-all duration-300 transform hover:-translate-y-2 border-2 border-indigo-100"
              >
                <div className="flex items-start mb-3 sm:mb-4">
                  <svg
                    className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-indigo-600 opacity-50"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <div className="mb-3 sm:mb-4 flex">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className="text-yellow-400 fill-current sm:w-5 sm:h-5"
                    />
                  ))}
                </div>
                <p className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-5 md:mb-6 font-medium">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center">
                  <div className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-base sm:text-lg flex-shrink-0">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="ml-2.5 sm:ml-3 min-w-0">
                    <div className="text-sm sm:text-base font-bold text-indigo-700 truncate">
                      {testimonial.name}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 truncate">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialProof;
